import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { api } from "../lib/api";
import { SectionHeading } from "../components/kit";
import { AreaChart, Area, BarChart, Bar, LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend } from "recharts";

const STAT_METRICS = [
  ["Verified Businesses", "verified_businesses", "verified-businesses"],
  ["Active Requirements", "active_requirements", "active-requirements"],
  ["Live Opportunities", "opportunities", "live-opportunities"],
  ["Monthly Connections", "monthly_connections", "monthly-connections"],
];

export default function Intelligence() {
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState(null);
  const [metricData, setMetricData] = useState(null);
  const [metricLoading, setMetricLoading] = useState(false);

  useEffect(() => { api.get("/intelligence/summary").then((r) => setData(r.data)).catch(() => {}); }, []);

  const openMetric = (slug) => {
    setMetric(slug);
    setMetricData(null);
    setMetricLoading(true);
    api.get(`/intelligence/metric-details/${slug}`)
      .then((r) => setMetricData(r.data))
      .catch(() => setMetricData({ rows: [], note: "Details unavailable right now." }))
      .finally(() => setMetricLoading(false));
  };

  if (!data) return <div className="max-w-7xl mx-auto px-8 py-24 text-sm font-mono text-slate-500 animate-pulse">Loading market intelligence…</div>;

  const tt = { background: "#0F172A", border: "1px solid #1E293B", borderRadius: 4, fontSize: 12 };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-14" data-testid="intelligence-page">
      <SectionHeading align="left" kicker="Business Intelligence Dashboard" title={"SEE WHERE BUSINESS IS MOVING."}
        sub="Verified network data and AI-generated market signals — always clearly distinguished." testid="intelligence-page-heading" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="network-totals">
        {STAT_METRICS.map(([l, key, slug]) => (
          <button key={slug} onClick={() => openMetric(slug)} data-testid={`intel-stat-${slug}`}
            className="card-surface p-5 text-left hover:border-gold/60 transition-colors cursor-pointer group">
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 group-hover:text-slate-400">{l}</p>
            <p className="font-mono text-2xl font-semibold text-gold mt-2">{data.network_totals[key].toLocaleString()}</p>
            <p className="text-[9px] font-mono uppercase tracking-widest text-slate-600 group-hover:text-gold mt-1.5 transition-colors">Click for details ↓</p>
          </button>
        ))}
      </div>

      {metric && (
        <div className="fixed inset-0 z-50 bg-navy-950/85 backdrop-blur flex items-center justify-center p-4" onClick={() => setMetric(null)} data-testid="intel-metric-modal">
          <div className="card-surface hairline-gold w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Live network records</p>
                <h2 className="font-display text-xl font-bold text-slate-100 mt-1" data-testid="intel-metric-title">{metricData?.label || "Details"}</h2>
              </div>
              <button onClick={() => setMetric(null)} data-testid="intel-metric-close" className="text-slate-500 hover:text-slate-300" aria-label="Close"><X size={18} /></button>
            </div>
            {metricLoading && <div className="py-10 flex justify-center" data-testid="intel-metric-loading"><Loader2 size={22} className="animate-spin text-gold" /></div>}
            {!metricLoading && metricData && (
              <div data-testid="intel-metric-content">
                {metricData.rows.length === 0 ? (
                  <p className="text-sm text-slate-400 py-4" data-testid="intel-metric-note-only">{metricData.note}</p>
                ) : (
                  <div className="divide-y divide-slate-800/60" data-testid="intel-metric-rows">
                    {metricData.rows.map((r, i) => (
                      <div key={i} className="py-2.5" data-testid={`intel-metric-row-${i}`}>
                        <p className="text-sm text-slate-200">{r.title}</p>
                        {r.meta && <p className="text-[11px] text-slate-500 mt-0.5">{r.meta}</p>}
                      </div>
                    ))}
                  </div>
                )}
                {metricData.note && metricData.rows.length > 0 && <p className="mt-4 text-[11px] text-slate-600" data-testid="intel-metric-note">{metricData.note}</p>}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-surface p-6 md:p-8" data-testid="chart-demand-trend">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1">Demand vs Supply vs Opportunity Volume</p>
          <p className="text-[10px] text-emerald-400 font-mono mb-5">VERIFIED DATA</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.demand_trend}>
                <XAxis dataKey="month" stroke="#475569" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#475569" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tt} /><Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="buyer_demand" stroke="#D4AF37" fill="#D4AF37" fillOpacity={0.12} strokeWidth={2} name="Buyer demand" />
                <Area type="monotone" dataKey="supplier_availability" stroke="#60A5FA" fill="#60A5FA" fillOpacity={0.08} strokeWidth={2} name="Supplier availability" />
                <Area type="monotone" dataKey="opportunity_volume" stroke="#10B981" fill="#10B981" fillOpacity={0.06} strokeWidth={1.5} name="Opportunity volume" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-6 md:p-8" data-testid="chart-geographic-demand">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1">Geographic Demand Index</p>
          <p className="text-[10px] text-emerald-400 font-mono mb-5">VERIFIED DATA</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.geographic_demand} layout="vertical">
                <XAxis type="number" stroke="#475569" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="region" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} width={90} />
                <Tooltip contentStyle={tt} />
                <Bar dataKey="demand" fill="#D4AF37" radius={[0, 3, 3, 0]} name="Demand index" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-6 md:p-8" data-testid="chart-industry-demand">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1">Industry Demand Index</p>
          <p className="text-[10px] text-emerald-400 font-mono mb-5">VERIFIED DATA</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.industry_demand}>
                <XAxis dataKey="industry" stroke="#94A3B8" fontSize={10} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={50} />
                <YAxis stroke="#475569" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tt} />
                <Bar dataKey="index" fill="#2563EB" radius={[3, 3, 0, 0]} name="Demand index" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-6 md:p-8" data-testid="chart-lead-activity">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1">Lead & Connection Activity</p>
          <p className="text-[10px] text-emerald-400 font-mono mb-5">VERIFIED DATA</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.lead_activity}>
                <XAxis dataKey="week" stroke="#475569" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#475569" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tt} /><Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="leads" stroke="#D4AF37" strokeWidth={2} dot={{ r: 3 }} name="Leads" />
                <Line type="monotone" dataKey="connections" stroke="#60A5FA" strokeWidth={2} dot={{ r: 3 }} name="Connections" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="card-surface hairline-gold p-6 md:p-8 mt-6" data-testid="intelligence-market-signal">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs font-mono uppercase tracking-widest text-amber-400">Market Signal · AI-GENERATED ESTIMATE</p>
          <span className="font-mono text-emerald-400 text-sm">{data.market_signal.mom_change} MoM</span>
        </div>
        <h3 className="font-display text-2xl font-bold text-slate-100 mt-3">{data.market_signal.title}: {data.market_signal.category}</h3>
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Top Locations</p>
            <div className="flex flex-wrap gap-2">{data.market_signal.top_locations.map((l) => <span key={l} className="bg-navy-800 border border-slate-700 rounded-sm px-3 py-1.5 text-xs text-slate-300">{l}</span>)}</div>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Top Buyer Segments</p>
            <div className="flex flex-wrap gap-2">{data.market_signal.buyer_segments.map((l) => <span key={l} className="bg-navy-800 border border-slate-700 rounded-sm px-3 py-1.5 text-xs text-slate-300">{l}</span>)}</div>
          </div>
        </div>
        <p className="mt-5 text-[11px] text-slate-600">{data.market_signal.confidence}. AI signals are estimates derived from network activity — not verified facts.</p>
      </div>
    </div>
  );
}
